import { DomainModelData, DomainModelsData, baseAttributes } from "@domain/composition/composition";
import { Luminary, pendantLocation } from "./Luminaries";
import Cluster from "../composition/Cluster";
import Space from "@domain/composition/Space";
import Complement from "@domain/composition/Complement";
import Pendant from "@domain/composition/Pendant";
import Light from "@domain/composition/Light";
import Track from "@domain/accessories/Track";
import ElectricalConnection from "@domain/accessories/ElectricalConnection";
import Reflector from "@domain/accessories/Reflector";
import Base from "@domain/composition/Base";
import Counterweight from "@domain/accessories/Counterweight";
import Toggles from "@domain/toggles/Toggles";

const genericId = 4
export const luminaryId = 1

export const level2typologies = {
    trackId: 11,
    electricalConnectionId: 8
}

export const level3typologies = {
    reflectorId: 63,
    counterweightId: 60
}

export default class VibiaFactory {

    static createElement(data: DomainModelData, attributes?: baseAttributes): Cluster | Complement {
        this.checkDataExist(data)

        let element

        const hasNoArticleType = data?.articleType == undefined
        const isLuminary = hasNoArticleType || data?.articleType?.id == luminaryId
        const isGenericProduct = data?.articleType?.id == genericId

        if (isLuminary || isGenericProduct) {
            element = this.createLuminary(data, attributes)
        } else {
            element = this.createComplement(data, attributes)
        }

        return element
    }

    static createElementByReference(data: DomainModelData | Array<DomainModelData>, reference: string): ReturnType<typeof this.createElement> {
        if (Array.isArray(data)) {
            const model = reference.substring(0, 4)
            const domainData = data.find(item => item.model == model)
            return this.createElementByReference(domainData, reference)
        } else {
            const element = this.createElement(data)
            element.setAttributesByReference(reference)
            return element
        }
    }

    static fromJSON<T extends Base = Base>(domainJSON, subfamilyData: DomainModelsData): T {
        if (!subfamilyData) {
            throw new Error("No family data was provided")
        }

        const hasProps = Boolean(domainJSON?.props)
        const type = domainJSON?.type
        if (!hasProps || !type) {
            throw new Error("Incorrect domainJSON provided")
        }

        if (type !== "Space") {
            throw new Error("Can not create domain instance for this domainJSON type yet")
        }

        return Space.fromJSON(domainJSON, undefined, undefined, subfamilyData) as unknown as T
    }

    static clone<T extends Base = Base>(element: T): T {
        if (element.instanceofType("Light")) {
            const lightData = element.data
            const lightAttributes = element.getAttributes(true)
            const newCluster = VibiaFactory.createElement(lightData, lightAttributes) as Cluster
            const newLight = newCluster.getLights()[0].element
            if (Toggles.isEnabled('avoidPAsInToolkitListConfigurator') && (element as unknown as Light).getPsa()) {
                newLight.convertToPsa()
            }
            return newLight as unknown as T
        } else if (element.instanceofType("Cluster")) {
            const light = element.getLights()[0].element
            const lightData = light.data
            const lightAttributes = light.getAttributes(true)
            const newCluster = VibiaFactory.createElement(lightData, lightAttributes) as Cluster
            if (Toggles.isEnabled('avoidPAsInToolkitListConfigurator') && (element as unknown as Cluster).anyChildIsPsa()) {
                newCluster.convertToPSA()
            }
            return newCluster as unknown as T
        } else if (element.instanceofType("Complement")) {
            const accesoryData = element.data
            const accesoryAttributes = element.getAttributes(true)
            const newAccesory = VibiaFactory.createElement(accesoryData, accesoryAttributes)
            return newAccesory as unknown as T
        } else {
            throw `Cannot clone element of type ${element.getClassName()}`
        }
    }

    private static createLuminary(data: DomainModelData, attributes?: baseAttributes): Luminary {
        const isPendant = data.applicationLocation.some(elem => elem.id == pendantLocation)

        const lightAttributes = {
            code: data.model,
            attributes: attributes ?? {},
            renderAttributes: {
                scale: [1, 1, 1],
                intensity: 2,
                decay: 2,
                penumbra: 1
            }
        }

        const emptyCluster = new Cluster([])

        const light = isPendant
            ? new Pendant(lightAttributes, emptyCluster, null, data)
            : new Light(lightAttributes, emptyCluster, null, data)

        const cluster = emptyCluster.addElement({ element: light, options: { position: [0, 0, 0] } })

        return cluster
    }

    private static createComplement(data: DomainModelData, attributes?: baseAttributes) {
        let complement = null;
        const complementAttributes = {
            code: data.model,
            attributes: attributes ?? {}
        }
        const level2typologyId = data?.articleType?.level2?.id
        const level3typologyId = data?.articleType?.level3?.id

        if (level2typologies.trackId == level2typologyId) {
            complement = new Track(complementAttributes, undefined, undefined, data)
        } else if (level2typologies.electricalConnectionId == level2typologyId) {
            complement = new ElectricalConnection(complementAttributes, undefined, undefined, data)
        } else if (level3typologies.reflectorId == level3typologyId) {
            complement = new Reflector(complementAttributes, undefined, undefined, data)
        } else if (level3typologies.counterweightId === level3typologyId) {
            complement = new Counterweight(complementAttributes, undefined, undefined, data)
        } else {
            complement = new Complement(complementAttributes, null, null, data)
        }

        return complement
    }

    protected static checkDataExist(data: any) {
        if (!data) {
            throw 'Required data to define domain was not provided';
        }
    }
}